import { create } from "zustand";

/**
 * Whether the APK is busy with its stored catalog right now — downloading it,
 * or rebuilding stage B's normalised rows (see api/nativeCatalog.ts).
 *
 * It runs in the background and every screen keeps working from the previous
 * copy, but on a TV it takes minutes, so the app says so rather than leaving
 * someone to wonder (owner, 2026-09-20). Plain module state, not a query: the
 * work is started deep in the device API, far from any component.
 *
 * WHY THIS IS A SET AND NOT A BOOLEAN (owner, 2026-09-22: "optimizing notice
 * is off when you said still rebuilding"). Several callers overlap. The device
 * API remembers its catalog check for five minutes (ENSURE_TTL_MS), and a
 * rebuild on the projector takes six and a half — so a second caller running
 * while the first still rebuilds is not a rare race, it happens every time.
 * With one boolean, whichever caller finished first turned the notice off,
 * including one that had decided it had nothing to do. Each piece of work now
 * holds its own token and can only ever release that one, so the notice is on
 * while ANY of them is running and goes off when the last finishes.
 */
interface CatalogRebuildState {
  /** True while at least one download or rebuild is running. */
  rebuilding: boolean;
  /** When the current stretch of work began, for a caller that wants to age it. */
  startedAt: number | null;
}

export const useCatalogRebuild = create<CatalogRebuildState>(() => ({
  rebuilding: false,
  startedAt: null,
}));

const working = new Set<number>();
let nextToken = 1;

function publish(): void {
  const rebuilding = working.size > 0;
  if (rebuilding === useCatalogRebuild.getState().rebuilding) return;
  useCatalogRebuild.setState({ rebuilding, startedAt: rebuilding ? Date.now() : null });
}

/**
 * Say that a piece of catalog work has started, and take a token for it. The
 * caller MUST pass that token back to {@link endCatalogWork}, and can release
 * nothing else.
 */
export function beginCatalogWork(): number {
  const token = nextToken++;
  working.add(token);
  publish();
  return token;
}

/** Say that this piece of work is over. Releasing twice is harmless. */
export function endCatalogWork(token: number): void {
  if (!working.delete(token)) return;
  publish();
}
